import { useServices, useToast } from '@/app/providers';
import { ACCESS_LEVEL_ERROR_CODES, type AccessLevel } from '@yawi/types';
import { ApiError } from '@yawi/infrastructure';
import { useCallback, useEffect, useRef, useState } from 'react';

export function useAccessLevels(tenantId: string) {
  const { accessLevelRepository, integrationCatalogRepository } = useServices();
  const { showToast } = useToast();

  const [accessLevels, setAccessLevels] = useState<AccessLevel[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);
  const [integrationId, setIntegrationId] = useState<string | null>(null);

  const resolveIntegrationId = useCallback(async (): Promise<string | null> => {
    try {
      const catalog = await integrationCatalogRepository.getCatalog(tenantId);
      for (const group of catalog.groups) {
        const item = group.items.find(i => i.provider === 'HIKCENTRAL');
        if (item?.integrationId) return item.integrationId;
      }
      return null;
    } catch {
      return null;
    }
  }, [integrationCatalogRepository, tenantId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [levels, resolvedId] = await Promise.all([
        accessLevelRepository.list(tenantId),
        integrationId === null ? resolveIntegrationId() : Promise.resolve(integrationId),
      ]);
      setAccessLevels(levels);
      if (resolvedId !== null) setIntegrationId(resolvedId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error al cargar niveles de acceso';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [accessLevelRepository, tenantId, integrationId, resolveIntegrationId]);

  useEffect(() => {
    load();
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  const refresh = useCallback(async () => {
    try {
      await accessLevelRepository.refresh(tenantId);
      const levels = await accessLevelRepository.list(tenantId);
      setAccessLevels(levels);
      setRefreshedAt(Date.now());
    } catch (e) {
      if (ApiError.isApiError(e) && e.statusCode === 429) {
        showToast('warning', 'Refrescado recientemente. Espera un minuto.');
        return;
      }
      const msg = e instanceof Error ? e.message : 'Error al refrescar';
      showToast('error', msg);
    }
  }, [accessLevelRepository, tenantId, showToast]);

  const setDefault = useCallback(async (accessLevelId: string): Promise<boolean> => {
    if (!integrationId) {
      showToast('error', 'No se encontró la integración HikCentral para este tenant.');
      return false;
    }
    setUpdating(true);
    try {
      await accessLevelRepository.setDefault(integrationId, accessLevelId, tenantId);
      setAccessLevels(prev =>
        prev.map(al => ({ ...al, isDefault: al.id === accessLevelId }))
      );
      showToast('success', 'Nivel de acceso predeterminado actualizado.');
      return true;
    } catch (e) {
      if (ApiError.isApiError(e)) {
        if (e.code === ACCESS_LEVEL_ERROR_CODES.NOT_FOUND) {
          showToast('error', 'El nivel de acceso seleccionado no existe en HikCentral.');
        } else if (e.code === ACCESS_LEVEL_ERROR_CODES.PROVIDER_UNSUPPORTED) {
          showToast('error', 'Este proveedor no soporta niveles de acceso.');
        } else {
          showToast('error', e.message);
        }
      } else {
        const msg = e instanceof Error ? e.message : 'Error al actualizar nivel de acceso';
        showToast('error', msg);
      }
      return false;
    } finally {
      setUpdating(false);
    }
  }, [accessLevelRepository, integrationId, tenantId, showToast]);

  return {
    accessLevels,
    loading,
    updating,
    error,
    refreshedAt,
    integrationId,
    refresh,
    setDefault,
    reload,
  };
}
